import { useCallback } from 'react'

import { copyToClipboard, type ToastLike } from '@mangovoice/mango-ui'

import type { UserApi } from '../api/userApi'
import { useResetPassword } from './useUserMutations'

export interface ResetPasswordWithClipboardMessages {
  copiedTitle: string
  copyFailedTitle: string
  manualCopyDescription: (url: string) => string
  missingUrlTitle: string
  missingUrlDescription: string
  requestFailedTitle: string
  unexpectedErrorDescription: string
}

export interface UseResetPasswordWithClipboardOptions {
  api: UserApi
  toast: ToastLike
  messages: ResetPasswordWithClipboardMessages
}

export interface UseResetPasswordWithClipboardResult {
  resetPassword: (userId: number) => void
  isResetPasswordLoading: boolean
}

export const useResetPasswordWithClipboard = ({
  api,
  toast,
  messages,
}: UseResetPasswordWithClipboardOptions): UseResetPasswordWithClipboardResult => {
  const resetPasswordMutation = useResetPassword({ api })

  const resetPassword = useCallback(
    (userId: number) => {
      resetPasswordMutation.mutate(
        { userId },
        {
          onSuccess: async (data) => {
            const resetUrl = data?.reset_url

            if (!resetUrl) {
              toast.create({
                title: messages.missingUrlTitle,
                description: messages.missingUrlDescription,
                type: 'warning',
                duration: 5000,
              })
              return
            }

            try {
              // Uses an async-API-then-execCommand fallback so the copy still
              // works inside the admin3 iframe after the mutation resolves
              // (the user-activation token has expired by `onSuccess`, which
              // makes the bare `navigator.clipboard.writeText` reject).
              await copyToClipboard(resetUrl)
              toast.create({
                title: messages.copiedTitle,
                type: 'success',
                duration: 4000,
              })
            } catch {
              toast.create({
                title: messages.copyFailedTitle,
                description: messages.manualCopyDescription(resetUrl),
                type: 'warning',
                duration: 7000,
              })
            }
          },
          onError: (err) => {
            toast.create({
              title: messages.requestFailedTitle,
              description: err.message || messages.unexpectedErrorDescription,
              type: 'warning',
              duration: 5000,
            })
          },
        },
      )
    },
    [messages, resetPasswordMutation, toast],
  )

  return {
    resetPassword,
    isResetPasswordLoading: resetPasswordMutation.isPending,
  }
}
